// eslint-disable-next-line jira/restricted/react
import React, { PureComponent, type MouseEvent, type SyntheticEvent } from 'react';

import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { ReactRenderer as AkRenderer } from '@atlaskit/renderer';

import { getEmojiProviderWithCustomEmoji } from '@atlassian/jira-common-atlaskit-services/src/emoji';
import checkForHref from '@atlassian/jira-common-components-html-view/src/check-for-href';
import iframeRedirect from '@atlassian/jira-common-navigation/src/iframe-redirect';
import { ff } from '@atlassian/jira-feature-flagging';
import { EmptyValue } from '@atlassian/jira-issue-view-common-styles';
import { isCmdOrCtrlClick, isMiddleClick } from '@atlassian/jira-issue-view-common-utils';
import { isExternalLink, smartLinksDefault } from '@atlassian/jira-linking-platform-utils';
import {
	removeCollectionFromAdf,
	isOnlyWhitespaceAdf,
	type ADF,
} from '@atlassian/jira-rich-content';
import {
	getTTRSeverityTrackingConfiguration,
	getUnsupportedContentLevelsTrackingConfiguration,
	isSpecBasedValidatorEnabled,
	isRendererTtiTrackingEnabled,
	isAltTextOnImagesEnabled,
} from './feature-flags';
import { mediaFeatureFlags } from './media-feature-flags';
import type { EventHandlers, InlineCardClickHandler, Props } from './types';

// eslint-disable-next-line jira/react/no-class-components
export default class Renderer extends PureComponent<Props> {
	static displayName = 'Renderer';

	static defaultProps = {
		adf: undefined,
		placeholder: undefined,
	};

	constructor(props: Props) {
		super(props);
		const { contextIdentifier, mediaProvider, mentionProvider, profileCardProvider } = this.props;

		this.node = null;

		this.dataProviders = new ProviderFactory();
		this.dataProviders.setProvider('mediaProvider', mediaProvider);
		this.dataProviders.setProvider('mentionProvider', Promise.resolve(mentionProvider));

		if (!__SERVER__) {
			this.dataProviders.setProvider(
				'emojiProvider',
				getEmojiProviderWithCustomEmoji(this.props.cloudId, this.props.loggedInUserAccountId),
			);
		}

		if (contextIdentifier) {
			this.dataProviders.setProvider(
				'contextIdentifierProvider',
				Promise.resolve(contextIdentifier),
			);
		}

		this.dataProviders.setProvider('profilecardProvider', profileCardProvider);

		this.eventHandlers = {
			smartCard: {
				onClick: this.onInlineCardClicked,
			},
			link: {
				onClick: this.onInlineCardClicked,
			},
		};
	}

	onInlineCardClicked: InlineCardClickHandler = (e, url) => {
		const { linkActions } = this.props;
		const event = e || window.event;
		event.preventDefault();
		if (isExternalLink(url)) {
			window.open(url, '_blank', 'noopener,noreferrer');
			return;
		}
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-explicit-any
		const mouseEvent: MouseEvent<any> = event as any;
		const shouldOpenInNewTab = isCmdOrCtrlClick(mouseEvent) || isMiddleClick(mouseEvent);
		iframeRedirect(url, shouldOpenInNewTab, linkActions.push);
	};

	onContentSelected = (e: SyntheticEvent<HTMLElement>) => {
		if (
			!(e.target instanceof window.Element) ||
			checkForHref(e.target, e.currentTarget) ||
			this.checkForMedia(e.target, e.currentTarget)
		) {
			// This is to avoid entering edit mode when clicking on a link or a video.
			e.stopPropagation();
		}
	};

	checkForMedia = (currentElement: Element | null, topElement: Element): boolean => {
		// If we reach the original div, we didn't find a video.
		if (!currentElement || currentElement === topElement) {
			return false;
		}
		// TODO: These checks are unsafe. Use the method outlined in
		// https://jdog.jira-dev.com/browse/BENTO-6367
		if (
			currentElement.className.includes?.('rich-media-item') ||
			currentElement.className.includes?.('media-file-card-view') ||
			currentElement.className.includes?.('media-viewer-popup')
		) {
			return true;
		}
		// Continue walking up the DOM tree looking for a video.
		return this.checkForMedia(currentElement.parentElement, topElement);
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	dataProviders: any;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	node: any;

	eventHandlers: EventHandlers;

	isEmpty = (): boolean => this.isAdfValueEmpty();

	isAdfValueEmpty = (): boolean => {
		const { adf } = this.props;
		// We should change this to an empty ADF object after this ticket is done: https://product-fabric.atlassian.net/browse/CS-235
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		return !adf || isOnlyWhitespaceAdf(adf as ADF);
	};

	setNodeRef = (element: HTMLElement | null) => {
		this.node = element;
	};

	render() {
		const { adf, placeholder } = this.props;

		if (this.isEmpty()) {
			return <EmptyValue>{placeholder}</EmptyValue>;
		}

		return (
			<div
				role="presentation"
				onClick={this.onContentSelected}
				onKeyPress={this.onContentSelected}
				ref={this.setNodeRef}
			>
				{isAltTextOnImagesEnabled() ? (
					<AkRenderer
						appearance="comment"
						schema={defaultSchema}
						document={removeCollectionFromAdf(adf)}
						dataProviders={this.dataProviders}
						eventHandlers={this.eventHandlers}
						allowAltTextOnImages
						allowWrapCodeBlock
						allowCopyToClipboard
						media={{
							allowLinking: true,
							featureFlags: mediaFeatureFlags(),
						}}
						portal={this.node}
						disableHeadingIDs
						analyticsEventSeverityTracking={getTTRSeverityTrackingConfiguration()}
						unsupportedContentLevelsTracking={getUnsupportedContentLevelsTrackingConfiguration()}
						useSpecBasedValidator={isSpecBasedValidatorEnabled()}
						featureFlags={{
							codeBidiWarnings: true,
							'code-bidi-warnings': true,
							'renderer-tti-tracking': isRendererTtiTrackingEnabled(),
							'allow-windowed-code-block': true,
						}}
						smartLinks={{
							showAuthTooltip: true,
							...(ff('platform.linking-platform.jira-renderer-use-default-smartlinks-value')
								? smartLinksDefault
								: {}),
						}}
					/>
				) : (
					<AkRenderer
						appearance="comment"
						schema={defaultSchema}
						document={removeCollectionFromAdf(adf)}
						dataProviders={this.dataProviders}
						eventHandlers={this.eventHandlers}
						allowAltTextOnImages
						allowWrapCodeBlock
						allowCopyToClipboard
						media={{
							allowLinking: true,
							featureFlags: mediaFeatureFlags(),
						}}
						portal={this.node}
						disableHeadingIDs
						analyticsEventSeverityTracking={getTTRSeverityTrackingConfiguration()}
						unsupportedContentLevelsTracking={getUnsupportedContentLevelsTrackingConfiguration()}
						useSpecBasedValidator={isSpecBasedValidatorEnabled()}
						featureFlags={{
							codeBidiWarnings: true,
							'code-bidi-warnings': true,
							'renderer-tti-tracking': isRendererTtiTrackingEnabled(),
							'allow-windowed-code-block': true,
						}}
						smartLinks={{
							showAuthTooltip: true,
							...(ff('platform.linking-platform.jira-renderer-use-default-smartlinks-value')
								? smartLinksDefault
								: {}),
						}}
					/>
				)}
			</div>
		);
	}
}
